import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { IconPlus } from "@tabler/icons-react";
import ContractValuesFields from "components/contracts/ContractValuesFields";
import { loanActionPermissions } from "utils/contract-loans";

const contractTabsSx = {
  minHeight: 40,
  borderBottom: 1,
  borderColor: "divider",
  "& .MuiTab-root": {
    minHeight: 40,
    minWidth: "auto",
    px: 2,
    textTransform: "none",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  "& .Mui-selected": {
    fontWeight: 600,
    backgroundColor: "action.selected",
  },
  "& .MuiTab-root[data-testid='add-contract-tab']": {
    minWidth: 40,
    width: 40,
    maxWidth: 40,
    px: 0,
  },
};

export const ContractProcessTabs = ({
  value,
  onChange,
  bulkMode = false,
  showAdd = false,
  addDisabled = false,
  extraTabs = [],
}) => (
  <Tabs
    value={value}
    onChange={onChange}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    data-testid="process-tabs"
    sx={contractTabsSx}
  >
    <Tab label="Dados gerais" value={0} />
    <Tab label="Valores" value={1} />
    <Tab
      label={bulkMode ? "Cálculo inicial (somente individual)" : "Cálculo inicial"}
      value={2}
      disabled={bulkMode}
    />
    {showAdd && (
      <Tab
        value="add"
        aria-label="Adicionar contrato"
        data-testid="add-contract-tab"
        disabled={addDisabled}
        icon={<IconPlus size={16} />}
      />
    )}
    {extraTabs.map((tab) => (
      <Tab
        key={tab.value}
        value={tab.value}
        title={tab.label}
        label={(
          <Box
            component="span"
            sx={{
              display: "block",
              maxWidth: 180,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </Box>
        )}
      />
    ))}
  </Tabs>
);

const ChildLoanForm = ({
  childContract,
  setChildContract,
  contractCategories,
  canEdit,
  onLookupAverageRate,
  valuesProps,
}) => (
  <Box>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} data-testid="child-number-field">
        <TextField
          label="Nº contrato *"
          value={childContract.number || ""}
          onChange={(event) => setChildContract({ ...childContract, number: event.target.value })}
          fullWidth
          inputProps={{ maxLength: 30 }}
          disabled={!canEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={contractCategories}
          getOptionLabel={(option) => option.label}
          value={contractCategories.find((item) => item.value === childContract.category) || null}
          onChange={(event, newValue) =>
            setChildContract({ ...childContract, category: newValue ? newValue.value : null })
          }
          disabled={!canEdit}
          renderInput={(params) => <TextField {...params} label="Categoria" fullWidth />}
        />
      </Grid>
    </Grid>
    <Box sx={{ mt: 2 }}>
      <ContractValuesFields
        contract={childContract}
        setContract={setChildContract}
        {...valuesProps}
        onLookupAverageRate={onLookupAverageRate}
      />
    </Box>
  </Box>
);

export const ContractEditorActions = ({
  loanActive = false,
  canEdit = false,
  canDelete = false,
  saving = false,
  onClose,
  onSavePrincipal,
  onSaveChild,
  onCancelChild,
  onDeleteChild,
}) => {
  if (!loanActive) {
    return (
      <>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={onSavePrincipal} color="primary">
          Salvar
        </Button>
      </>
    );
  }

  return (
    <>
      {canDelete ? (
        <Button variant="outlined" color="error" onClick={onDeleteChild} disabled={saving}>
          Excluir
        </Button>
      ) : (
        <Box />
      )}
      <Stack direction="row" spacing={1}>
        <Button color="secondary" onClick={onCancelChild} disabled={saving}>
          Cancelar
        </Button>
        {canEdit && (
          <Button variant="contained" color="primary" onClick={onSaveChild} disabled={saving}>
            Salvar contrato
          </Button>
        )}
      </Stack>
    </>
  );
};

export const ChildContractPane = ({
  loanEditor,
  contractCategories,
  contractTypes,
  isFieldDisabled,
  hasError,
  calculatePgto,
  calculateInstallmentQuestionedValue,
  calculateYearlyInterest,
  calculateDaysDifference,
  isFileOnlyMode = false,
  bulkMode = false,
  childSection = "values",
  onChildSection,
  calculationBody = null,
}) => {
  const selection = loanEditor?.selection || { kind: "principal" };
  const permissions = loanActionPermissions(loanEditor?.permissions, {
    isFileOnlyMode,
    bulkMode,
    isPrincipal: false,
  });
  const childContract = loanEditor?.childContract;
  if (!childContract) return null;
  if (selection.kind === "child" && childContract.id !== selection.childId) return null;
  if (selection.kind === "new" && childContract.id) return null;

  const childFieldDisabled = (field) => !permissions.canEdit || isFieldDisabled(field);
  const valuesProps = {
    contractTypes,
    isFieldDisabled: childFieldDisabled,
    hasError,
    calculatePgto,
    calculateInstallmentQuestionedValue,
    calculateYearlyInterest,
    calculateDaysDifference,
    onLookupAverageRate: loanEditor.onLookupChildAverageRate,
  };

  const section = childSection === "calculation" ? "calculation" : "values";

  return (
    <Box data-testid="child-contract-pane">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Contrato adicional
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={section}
        onChange={(event, newValue) => {
          if (newValue) onChildSection?.(newValue);
        }}
        data-testid="child-section-toggle"
        sx={{
          mb: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          p: 0.25,
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "6px !important",
            textTransform: "none",
            px: 1.5,
            py: 0.25,
            minHeight: 32,
            color: "text.secondary",
          },
          "& .MuiToggleButton-root.Mui-selected": {
            bgcolor: "background.paper",
            color: "text.primary",
            fontWeight: 600,
            "&:hover": { bgcolor: "background.paper" },
          },
        }}
      >
        <ToggleButton value="values">Valores</ToggleButton>
        <ToggleButton value="calculation">Cálculo inicial</ToggleButton>
      </ToggleButtonGroup>
      {section === "calculation" ? calculationBody : (
        <ChildLoanForm
          childContract={childContract}
          setChildContract={loanEditor.setChildContract}
          contractCategories={contractCategories}
          canEdit={permissions.canEdit}
          onLookupAverageRate={loanEditor.onLookupChildAverageRate}
          valuesProps={valuesProps}
        />
      )}
    </Box>
  );
};

export default ContractProcessTabs;
